import GradientColorPicker, {
  DEFAULT_GRADIENT_VALUE,
  parseValues,
} from 'lib/ui/form/BackgroundPicker/GradientColorPicker'
import styled from 'styled-components'
import ImagePicker from 'lib/ui/form/BackgroundPicker/ImagePicker'
import SolidColorPicker from 'lib/ui/form/BackgroundPicker/SolidColorPicker'
import React, {useEffect, useState} from 'react'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import TabPanel from 'lib/ui/tabs/TabPanel'
import {isNull} from 'lodash'
import {hex} from 'lib/color'

export type BackgroundPickerProps = {
  label: string
  background?: string
  onChange: (background: string) => void
  disabled?: boolean
  hasFreeSize?: boolean
  className?: string
}

const SOLID = 0
const GRADIENT = 1
const IMAGE = 2

type BackgroundType = typeof SOLID | typeof GRADIENT | typeof IMAGE

export default function BackgroundPicker(props: BackgroundPickerProps) {
  const {background, onChange} = props
  const [type, setType] = useState<BackgroundType | null>(null)

  const [prevSolidBackground, setPrevSolidBackground] = useState<
    string | undefined
  >(undefined)
  const [prevGradientBackground, setPrevGradientBackground] = useState<
    string | undefined
  >(undefined)
  const [prevImageBackground, setPrevImageBackground] = useState<
    string | undefined
  >(undefined)

  const isGradient = background?.startsWith('linear-gradient')
  const isImage = background?.startsWith('url')

  // Load initial type
  useEffect(() => {
    if (!isNull(type)) {
      return
    }

    if (isImage) {
      setType(IMAGE)
      return
    }

    if (isGradient) {
      setType(GRADIENT)
      return
    }

    setType(SOLID)
  }, [isImage, isGradient, type, setType])

  // Handle switching types by remembering the current value under the type
  // we're leaving, then seeding the new type with a sensible value: a
  // previously-stashed value for that type, or — for solid<->gradient — a
  // value derived from the current background so the user's picked color
  // carries over.
  const handleTypeChange = (
    _: React.ChangeEvent<{}>,
    nextType: BackgroundType,
  ) => {
    if (isNull(type) || nextType === type) {
      return
    }

    // Stash the current background under the type we're leaving so that if
    // the user later switches back to that type we can restore exactly what
    // they had — flipping tabs shouldn't discard their work.
    if (type === SOLID) {
      setPrevSolidBackground(background)
    }
    if (type === GRADIENT) {
      setPrevGradientBackground(background)
    }
    if (type === IMAGE) {
      setPrevImageBackground(background)
    }

    onChange(
      nextBackground({
        fromType: type,
        nextType,
        background,
        prevSolidBackground,
        prevGradientBackground,
        prevImageBackground,
      }),
    )

    setType(nextType)
  }

  if (isNull(type)) {
    return null
  }

  return (
    <Box className={props.className}>
      <StyledTabs
        value={type}
        onChange={handleTypeChange}
        TabIndicatorProps={{
          style: {
            display: 'none',
          },
        }}
        scrollButtons="off"
      >
        <StyledTab
          aria-label="set solid background"
          label="Solid"
          disableRipple
          value={SOLID}
        />
        <StyledTab
          aria-label="set gradient background"
          label="Gradient"
          disableRipple
          value={GRADIENT}
        />
        <StyledTab
          aria-label="set image background"
          label="Image"
          disableRipple
          value={IMAGE}
        />
      </StyledTabs>

      <StyledTabPanel currentIndex={type} index={SOLID}>
        <SolidColorPicker {...props} />
      </StyledTabPanel>

      <StyledTabPanel currentIndex={type} index={GRADIENT}>
        <GradientColorPicker {...props} />
      </StyledTabPanel>

      <StyledTabPanel currentIndex={type} index={IMAGE}>
        <ImagePicker {...props} />
      </StyledTabPanel>
    </Box>
  )
}

// Picks the value to apply when switching tabs.
//
// - solid -> gradient: seed color1 from the current solid color so the user
//   doesn't lose their picked color; reuse degrees/color2 from the previous
//   gradient if we have one, otherwise the default gradient.
// - gradient -> solid: extract color1 from the current gradient.
// - everything else: restore the previously-seen value for the destination
//   type so flipping tabs doesn't discard the user's work.
function nextBackground(params: {
  fromType: BackgroundType
  nextType: BackgroundType
  background: string | undefined
  prevSolidBackground: string | undefined
  prevGradientBackground: string | undefined
  prevImageBackground: string | undefined
}): string {
  const {
    fromType,
    nextType,
    background,
    prevSolidBackground,
    prevGradientBackground,
    prevImageBackground,
  } = params

  if (nextType === GRADIENT && fromType === SOLID) {
    return gradientFromSolid(background, prevGradientBackground)
  }

  if (nextType === SOLID && fromType === GRADIENT) {
    return parseValues(background).color1
  }

  if (nextType === GRADIENT) {
    return prevGradientBackground ?? ''
  }

  if (nextType === SOLID) {
    return prevSolidBackground ?? ''
  }

  if (nextType === IMAGE) {
    return prevImageBackground ?? ''
  }

  return ''
}

// Build a gradient from the current solid color, reusing the user's previous
// gradient direction/second-color when available so flipping back to gradient
// feels continuous instead of resetting to defaults.
function gradientFromSolid(
  currentBackground: string | undefined,
  prevGradientBackground: string | undefined,
) {
  const color1 = hex(currentBackground) || '#FFFFFF'

  if (prevGradientBackground == null) {
    const {degrees, color2} = DEFAULT_GRADIENT_VALUE
    return `linear-gradient(${degrees}deg,${color1},${color2})`
  }

  const {degrees, color2} = parseValues(prevGradientBackground)
  return `linear-gradient(${degrees}deg,${color1},${color2})`
}

// We're going to export the container here in case any parent needs to override
// the style via CSS. ie., instead a config panel where we would like to give
// the input negative margins.
export const Box = styled.div``

const StyledTabs = styled(Tabs)`
  background: ${(props) => props.theme.colors.input.background};
  padding: 2px;
  min-height: auto !important;

  // Container
  > div > div {
    justify-content: space-between;
    padding: 0 ${(props) => props.theme.spacing[4]};
  }
`

const StyledTab = styled(Tab)`
  background-color: ${(props) => props.theme.colors.input.dark};
  margin: 0px 1px !important;
  max-width: 100%;
  min-width: auto !important;
  min-height: auto !important;
  border-radius: 4px;
  font-style: normal;
  font-weight: normal;
  font-size: 14px !important;
  line-height: 18px !important;
  padding: ${(props) =>
    `${props.theme.spacing[2]} ${props.theme.spacing[4]}`} !important;

  &.Mui-selected {
    background-color: ${(props) => props.theme.colors.panel.background};
    border-radius: 4px;
  }
`

const StyledTabPanel = styled(TabPanel)`
  padding: ${(props) => `${props.theme.spacing[6]} ${props.theme.spacing[5]}`} 0 !important;
`
